import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { postService, Post } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import EditPostModal from "../components/EditPostModal";
import DeletePostButton from "../components/DeletePostButton";
import EditProfileModal from "../components/EditProfileModal";
import FollowersModal from "../components/FollowersModal";
import {
  User as UserIcon,
  Calendar,
  Users,
  FileText,
  TrendingUp,
  Eye,
  Star,
  MessageCircle,
  Clock,
  PenTool,
  Edit,
  Search,
  Filter,
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { enGB } from "date-fns/locale";
import toast from "react-hot-toast";

const MyProfile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);

  // New state for advanced features
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [showArchived, setShowArchived] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // New state for followers modal
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followersModalType, setFollowersModalType] = useState<"followers" | "following">("followers");

  const [stats, setStats] = useState({
    totalViews: 0,
    totalRatings: 0,
    averageRating: 0,
    totalComments: 0,
  });

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const togglePost = (postId: string) => {
    setExpanded((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  useEffect(() => {
    if (user) {
      loadUserPosts();
    }
  }, [user, sortBy, searchTerm, showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserPosts = async () => {
    try {
      if (user) {
        setLoading(true);

        // Use the new getUserPosts API method with filters
        const userPosts = await postService.getUserPosts(
          user.id,
          0,
          100,
          sortBy,
          searchTerm || undefined,
          showArchived
        );

        setPosts(userPosts);

        const summary = await postService.getUserPostsSummary(user.id);
        setStats(summary);
      }
    } catch (error) {
      toast.error("Error loading your articles");
    } finally {
      setLoading(false);
    }
  };

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  const handleEditSuccess = async (updatedPost: Post) => {
    setEditingPost(null);
    try {
      await loadUserPosts();
    } catch (e) {
      setPosts((prev) => {
        const updated = prev.map((post) => (post.id === updatedPost.id ? updatedPost : post));
        return updated.filter((p) => (showArchived ? p.archived : !p.archived));
      });
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      await postService.deletePost(postId);
      setPosts(posts.filter((post) => post.id !== postId));
    } catch (error) {
      throw error; // Re-throw to be handled by DeletePostButton
    }
  };

  const handleProfileUpdateSuccess = () => {
    setEditingProfile(false);
    // User data will be updated via auth context
  };

  const handleArchivePost = async (postId: string) => {
    try {
      const result = await postService.toggleArchivePost(postId);
      toast.success(result.message);

      // Refresh posts list
      await loadUserPosts();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error archiving article");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
    setShowFilters(false);
  };

  const getSortLabel = (sortValue: string) => {
    const labels = {
      date_desc: "Most recent",
      date_asc: "Oldest",
      visits_desc: "Most visited",
      visits_asc: "Least visited",
      rating_desc: "Best rated",
      rating_asc: "Lowest rated",
    };
    return labels[sortValue as keyof typeof labels] || "Most recent";
  };

  const openFollowersModal = (type: "followers" | "following") => {
    setFollowersModalType(type);
    setShowFollowersModal(true);
  };

  const closeFollowersModal = () => {
    setShowFollowersModal(false);
  };

  if (!user) {
    return (
      <div className="beat-container">
        <div className="beat-error">
          <h2 className="text-lg font-semibold mb-2">Access restricted</h2>
          <p>You must sign in to view your profile.</p>
          <Link to="/login" className="beat-button mt-4 inline-block">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="beat-container">
      {/* Profile Header */}
      <div className="my-profile-header px-1 md:px-14 w-full inline-flex flex-col md:flex-row justify-center items-start gap-[12px] sm:gap-4 lg:gap-[52px] pt-[16px] sm:pt-6 lg:pt-10 pb-[16px] sm:pb-6 lg:pb-0">
        {/* Avatar */}
        <div className="relative w-[96px] h-[96px] sm:w-[120px] sm:h-[120px] lg:w-[167px] lg:h-[167px] shrink-0">
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={user.username}
              className="w-full h-full rounded-full object-cover"
              style={{ background: "#D9D9D9" }}
            />
          ) : (
            <div className="w-full h-full rounded-full bg-[#D9D9D9] flex items-center justify-center">
              <UserIcon className="w-12 h-12 sm:w-16 sm:h-16" style={{ color: "#666" }} />
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div
          style={{
            width: "100%",
            background: "rgba(0, 0, 0, 0)",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: "7px",
            display: "inline-flex",
          }}
        >
          {/* Name and Rating */}
          <div className="my-profile-name-rating inline-flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-between gap-[10px] sm:gap-6">
            <div
              className="w-full sm:w-[296px] flex flex-col justify-center text-[26px] sm:text-[28px] md:text-[32px] lg:text-[36px] leading-[32px] sm:leading-[34px] md:leading-[38px] lg:leading-[40px]"
              style={{
                color: "#262626",
                fontFamily: "Abhaya Libre",
                fontWeight: "700",
                letterSpacing: "0.72px",
                wordWrap: "break-word",
              }}
            >
              {user.username}
            </div>
            <div style={{ justifyContent: "flex-start", alignItems: "center", gap: "4px", display: "flex" }}>
              <Star className="w-4 h-4" style={{ color: "#737373" }} />

              <div
                className="text-[13px] sm:text-[14px]"
                style={{ color: "#404040", fontFamily: "Inter", fontWeight: "400", wordWrap: "break-word" }}
              >
                {Math.round(user.mojo * 10) / 10}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="my-profile-stats-cards inline-flex flex-wrap sm:flex-nowrap items-center justify-center sm:justify-start gap-[5px] sm:gap-5">
            {/* Mojo Card */}
            <div className="w-full sm:w-[204px] px-2.5 sm:px-[10px] py-[6px] sm:py-[19px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 inline-flex flex-col items-center justify-start gap-[6px] sm:gap-[7px]">
              <div className="relative overflow-hidden w-[28px] h-[28px] sm:w-[34px] sm:h-[34px]">
                <img src="/mojo_icon.png" alt="Mojo" style={{ width: "100%", height: "100%" }} />
              </div>
              <div className="w-[120px] sm:w-[139px] flex flex-col items-center justify-start gap-[8px] sm:gap-[10px]">
                <div
                  className="text-center text-[22px] sm:text-[28px] leading-[18px] sm:leading-[20px]"
                  style={{ color: "black", fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
                >
                  {Math.round(user.mojo)}
                </div>
                <div
                  className="flex flex-col justify-center text-center text-[12px] sm:text-[14px]"
                  style={{ color: "black", fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
                >
                  Mojo
                </div>
              </div>
            </div>

            {/* Articles Card */}
            <div className="w-full sm:w-[205px] px-2.5 sm:px-[10px] py-[6px] sm:py-[19px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 inline-flex flex-col items-center justify-start gap-[6px] sm:gap-[7px]">
              <div className="relative overflow-hidden w-[28px] h-[28px] sm:w-[34px] sm:h-[34px]">
                <FileText style={{ width: "28px", height: "28px", color: "black" }} className="sm:w-8 sm:h-8" />
              </div>
              <div className="w-[120px] sm:w-[139px] flex flex-col items-center justify-start gap-[8px] sm:gap-[10px]">
                <div
                  className="text-center text-[22px] sm:text-[28px] leading-[18px] sm:leading-[20px]"
                  style={{ color: "black", fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
                >
                  {user.posts_count}
                </div>
                <div
                  className="flex flex-col justify-center text-center text-[12px] sm:text-[14px]"
                  style={{ color: "black", fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
                >
                  Articles
                </div>
              </div>
            </div>

            {/* Followers Card */}
            <button
              onClick={() => openFollowersModal("followers")}
              className="w-full sm:w-[205px] px-2.5 sm:px-[10px] py-[6px] sm:py-[19px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 inline-flex flex-col items-center justify-start gap-[6px] sm:gap-[7px] bg-transparent cursor-pointer"
            >
              <div className="relative overflow-hidden w-[28px] h-[28px] sm:w-[34px] sm:h-[34px]">
                <Users style={{ width: "28px", height: "28px", color: "black" }} className="sm:w-8 sm:h-8" />
              </div>
              <div className="w-[120px] sm:w-[139px] flex flex-col items-center justify-start gap-[8px] sm:gap-[10px]">
                <div
                  className="text-center text-[22px] sm:text-[28px] leading-[18px] sm:leading-[20px]"
                  style={{ color: "black", fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
                >
                  {user.followers_count}
                </div>
                <div
                  className="flex flex-col justify-center text-center text-[12px] sm:text-[14px]"
                  style={{ color: "black", fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
                >
                  Followers
                </div>
              </div>
            </button>

            {/* Following Card */}
            <button
              onClick={() => openFollowersModal("following")}
              className="w-full sm:w-[205px] px-2.5 sm:px-[10px] py-[6px] sm:py-[19px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 inline-flex flex-col items-center justify-start gap-[6px] sm:gap-[7px] bg-transparent cursor-pointer"
            >
              <div className="relative overflow-hidden w-[28px] h-[28px] sm:w-[34px] sm:h-[34px]">
                <Users style={{ width: "28px", height: "28px", color: "black" }} className="sm:w-8 sm:h-8" />
              </div>
              <div className="w-[120px] sm:w-[139px] flex flex-col items-center justify-start gap-[8px] sm:gap-[10px]">
                <div
                  className="text-center text-[22px] sm:text-[28px] leading-[18px] sm:leading-[20px]"
                  style={{ color: "black", fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
                >
                  {user.following_count}
                </div>
                <div
                  className="flex flex-col justify-center text-center text-[12px] sm:text-[14px]"
                  style={{ color: "black", fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
                >
                  Following
                </div>
              </div>
            </button>
          </div>

          {/* Bio */}
          <div
            className="w-full text-[14px] sm:text-[16px] md:text-[17px] lg:text-[18px] leading-[20px] sm:leading-[24px] md:leading-[26px] lg:leading-[28px]"
            style={{ color: "#525252", fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
          >
            {user.bio ||
              "Modern wanderer documenting the poetry of movement and the philosophy of the open road. Finding truth in the spaces between destinations."}
          </div>

          {/* Additional Info and Action Buttons */}
          <div className="w-full inline-flex flex-wrap items-center justify-start gap-[12px] sm:gap-[14px] md:gap-4 lg:gap-6">
            <div
              className="text-[13px] sm:text-[14px] leading-[18px] sm:leading-[20px]"
              style={{ color: "#737373", fontFamily: "Abhaya Libre", fontWeight: "400", wordWrap: "break-word" }}
            >
              {user.followers_count} Followers
            </div>
            <div
              className="text-[13px] sm:text-[14px] leading-[18px] sm:leading-[20px]"
              style={{ color: "#737373", fontFamily: "Inter", fontWeight: "400", wordWrap: "break-word" }}
            >
              •
            </div>
            <div
              className="text-[13px] sm:text-[14px] leading-[18px] sm:leading-[20px]"
              style={{ color: "#737373", fontFamily: "Abhaya Libre", fontWeight: "400", wordWrap: "break-word" }}
            >
              Joined {format(new Date(user.created_at), "MMMM yyyy", { locale: enGB })}
            </div>
            <div
              className="text-[13px] sm:text-[14px] leading-[18px] sm:leading-[20px]"
              style={{ color: "#737373", fontFamily: "Inter", fontWeight: "400", wordWrap: "break-word" }}
            >
              •
            </div>
            <div
              className="text-[13px] sm:text-[14px] leading-[18px] sm:leading-[20px]"
              style={{ color: "#737373", fontFamily: "Abhaya Libre", fontWeight: "400", wordWrap: "break-word" }}
            >
              {user.posts_count} Posts
            </div>

            {/* Action Buttons */}
            <div className="ml-0 sm:ml-auto w-full sm:w-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-[8px] sm:gap-[10px] lg:gap-3">
              <Link
                to="/write"
                className="w-full sm:w-auto px-4 py-2 sm:py-[9px] rounded-[49px] outline outline-[1px] outline-black -outline-offset-1 inline-flex justify-center items-center gap-[8px] sm:gap-[10px] bg-[#262626]"
              >
                <div
                  className="text-[13px] sm:text-[14px]"
                  style={{
                    color: "white",
                    fontFamily: "Abhaya Libre ExtraBold",
                    fontWeight: "800",
                    wordWrap: "break-word",
                  }}
                >
                  Write
                </div>
              </Link>
              <button
                onClick={() => setEditingProfile(true)}
                className="w-full sm:w-auto px-4 py-2 sm:py-[9px] rounded-[49px] outline outline-[1px] outline-black -outline-offset-1 inline-flex justify-center items-center gap-[8px] sm:gap-[10px] bg-transparent"
              >
                <div
                  className="text-[13px] sm:text-[14px]"
                  style={{
                    color: "black",
                    fontFamily: "Abhaya Libre ExtraBold",
                    fontWeight: "800",
                    wordWrap: "break-word",
                  }}
                >
                  Edit
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Separator */}
      <div
        className="hidden lg:block"
        style={{
          width: "100%",
          height: "1px",
          backgroundColor: "rgb(211, 211, 211)",
          marginTop: "40px",
          marginBottom: "40px",
        }}
      />

      {/* Stats Dashboard */}
      <div className="stats-dashboard w-full inline-flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-[5px] sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
        {/* Total Visits Card */}
        <div className="total-visits-card w-full sm:w-[220px] lg:w-[249px] px-[10px] py-[10px] md:py-[16px] lg:py-[19px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 inline-flex flex-col items-center justify-start gap-[6px] sm:gap-[7px]">
          <div className="relative overflow-hidden w-[28px] h-[28px] sm:w-[34px] sm:h-[34px]">
            <Eye style={{ color: "black" }} className="w-[28px] h-[28px] sm:w-8 sm:h-8" />
          </div>
          <div className="w-[120px] sm:w-[139px] flex flex-col items-center justify-start gap-[8px] sm:gap-[10px]">
            <div
              className="text-center text-[20px] sm:text-[22px] md:text-[26px] lg:text-[28px] leading-[18px] sm:leading-[20px]"
              style={{ color: "black", fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
            >
              {stats.totalViews.toLocaleString()}
            </div>
            <div
              className="flex flex-col justify-center text-center text-[12px] sm:text-[14px]"
              style={{ color: "black", fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
            >
              Total visits
            </div>
          </div>
        </div>

        {/* Average Rating Card */}
        <div className="average-rating-card w-full sm:w-[220px] lg:w-[249px] px-[10px] py-[10px] md:py-[16px] lg:py-[19px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 inline-flex flex-col items-center justify-start gap-[6px] sm:gap-[7px]">
          <div className="relative overflow-hidden w-[28px] h-[28px] sm:w-[34px] sm:h-[34px]">
            <Star style={{ color: "black" }} className="w-[28px] h-[28px] sm:w-8 sm:h-8" />
          </div>
          <div className="w-[120px] sm:w-[139px] flex flex-col items-center justify-start gap-[8px] sm:gap-[10px]">
            <div
              className="text-center text-[20px] sm:text-[22px] md:text-[26px] lg:text-[28px] leading-[18px] sm:leading-[20px]"
              style={{ color: "black", fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
            >
              {stats.averageRating.toFixed(1)}
            </div>
            <div
              className="flex flex-col justify-center text-center text-[12px] sm:text-[14px]"
              style={{ color: "black", fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
            >
              Average rating
            </div>
          </div>
        </div>

        {/* Comments Received Card */}
        <div className="comments-received-card w-full sm:w-[220px] lg:w-[249px] px-[10px] py-[10px] md:py-[16px] lg:py-[19px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 inline-flex flex-col items-center justify-start gap-[6px] sm:gap-[7px]">
          <div className="relative overflow-hidden w-[28px] h-[28px] sm:w-[34px] sm:h-[34px]">
            <MessageCircle style={{ color: "black" }} className="w-[28px] h-[28px] sm:w-8 sm:h-8" />
          </div>
          <div className="w-[120px] sm:w-[139px] flex flex-col items-center justify-start gap-[8px] sm:gap-[10px]">
            <div
              className="text-center text-[20px] sm:text-[22px] md:text-[26px] lg:text-[28px] leading-[18px] sm:leading-[20px]"
              style={{ color: "black", fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
            >
              {stats.totalComments}
            </div>
            <div
              className="flex flex-col justify-center text-center text-[12px] sm:text-[14px]"
              style={{ color: "black", fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
            >
              Comments received
            </div>
          </div>
        </div>

        {/* Ratings Received Card */}
        <div className="ratings-received-card w-full sm:w-[220px] lg:w-[249px] px-[10px] py-[10px] md:py-[16px] lg:py-[19px] rounded-[20px] outline outline-[1px] outline-[rgba(0,0,0,0.38)] -outline-offset-1 inline-flex flex-col items-center justify-start gap-[6px] sm:gap-[7px]">
          <div className="relative overflow-hidden w-[28px] h-[28px] sm:w-[34px] sm:h-[34px]">
            <TrendingUp style={{ color: "black" }} className="w-[28px] h-[28px] sm:w-8 sm:h-8" />
          </div>
          <div className="w-[120px] sm:w-[139px] flex flex-col items-center justify-start gap-[8px] sm:gap-[10px]">
            <div
              className="text-center text-[20px] sm:text-[22px] md:text-[26px] lg:text-[28px] leading-[18px] sm:leading-[20px]"
              style={{ color: "black", fontFamily: "Playfair Display", fontWeight: "600", wordWrap: "break-word" }}
            >
              {stats.totalRatings}
            </div>
            <div
              className="flex flex-col justify-center text-center text-[12px] sm:text-[14px]"
              style={{ color: "black", fontFamily: "Instrument Sans", fontWeight: "400", wordWrap: "break-word" }}
            >
              Ratings received
            </div>
          </div>
        </div>
      </div>

      {/* My Posts */}
      <div className="my-profile-my-posts">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-[10px] sm:gap-[12px] md:gap-4 lg:gap-[21px] mb-4 sm:mb-6">
          <div
            className="text-[18px] sm:text-[20px] md:text-[22px] lg:text-[24px] w-full sm:w-auto"
            style={{ color: "black", fontFamily: "Abhaya Libre ExtraBold", fontWeight: "800", wordWrap: "break-word" }}
          >
            {showArchived ? "Your archived articles" : "All your articles"} ({posts.length})
          </div>
          <Link
            to="/write"
            data-property-1="Variant2"
            className="w-full sm:w-auto px-4 py-2 sm:py-[9px] rounded-[49px] outline outline-[1px] outline-black -outline-offset-1 flex justify-center items-center gap-[8px] sm:gap-[10px] bg-[#262626]"
          >
            <div
              className="text-[13px] sm:text-[14px]"
              style={{
                color: "white",
                fontFamily: "Abhaya Libre ExtraBold",
                fontWeight: "800",
                wordWrap: "break-word",
              }}
            >
              Write a new article
            </div>
          </Link>
          <button
            onClick={() => setShowArchived(!showArchived)}
            data-property-1="Variant2"
            className="w-full sm:w-auto px-4 py-2 sm:py-[9px] rounded-[49px] outline outline-[1px] outline-black -outline-offset-1 flex justify-center items-center gap-[8px] sm:gap-[10px] bg-transparent"
          >
            <div
              className="text-[13px] sm:text-[14px]"
              style={{
                color: "black",
                fontFamily: "Abhaya Libre ExtraBold",
                fontWeight: "800",
                wordWrap: "break-word",
              }}
            >
              {showArchived ? "Show active articles" : "Show archived articles"}
            </div>
          </button>
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-newspaper-500 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by title, content or hashtags..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-3 border border-newspaper-300 rounded-lg focus:ring-2 focus:ring-newspaper-500 focus:border-transparent"
            />
          </div>

          {/* Filter Controls */}
          <div
            className="sort-by-time-period"
            style={{
              height: "37px",
              background: "rgba(0, 0, 0, 0)",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "16px",
              display: "inline-flex",
            }}
          >
            <div
              style={{
                width: "53px",
                height: "20px",
                color: "#404040",
                fontSize: "16px",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                wordWrap: "break-word",
              }}
            >
              Sort by:
            </div>
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                style={{
                  paddingLeft: "15px",
                  paddingRight: "40px",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                  background: "white",
                  borderRadius: "68px",
                  outline: "1px #D4D4D4 solid",
                  outlineOffset: "-1px",
                  color: "black",
                  fontSize: "14px",
                  fontFamily: "Abhaya Libre",
                  fontWeight: "400",
                  border: "none",
                  appearance: "none",
                  cursor: "pointer",
                  minWidth: "120px",
                }}
              >
                <option value="date_desc">Most recent</option>
                <option value="date_asc">Oldest</option>
                <option value="visits_desc">Most visited</option>
                <option value="visits_asc">Least visited</option>
                <option value="rating_desc">Best rated</option>
                <option value="rating_asc">Lowest rated</option>
              </select>
              <div
                style={{
                  position: "absolute",
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  width: "21px",
                  height: "21px",
                }}
              >
                <div
                  style={{
                    width: "10.52px",
                    height: "6.32px",
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "black",
                    clipPath: "polygon(0 0, 100% 0, 50% 100%)",
                  }}
                />
              </div>
            </div>

            {searchTerm && (
              <div
                style={{
                  color: "#404040",
                  fontSize: "14px",
                  fontFamily: "Abhaya Libre",
                  fontWeight: "400",
                  wordWrap: "break-word",
                }}
              >
                {posts.length} result{posts.length !== 1 ? "s" : ""} for "{searchTerm}"
              </div>
            )}
          </div>
        </div>

        {/* My Profile's Posts */}
        <div
          className="articles-list sm:space-y-4 md:space-y-6 px-4 sm:px-6 md:px-11"
          style={{
            paddingBottom: "60px",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            display: "flex",
          }}
        >
          {loading ? (
            <div className="space-y-6 w-full">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="beat-card p-6">
                  <div className="beat-loading h-6 mb-4"></div>
                  <div className="beat-loading h-4 mb-2"></div>
                  <div className="beat-loading h-32"></div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 mx-auto">
              {showArchived ? (
                <>
                  <Archive className="w-16 h-16 mx-auto mb-4 text-newspaper-400" />
                  <h3 className="text-xl font-semibold text-newspaper-700 mb-2">You have no archived articles</h3>
                  <p className="text-newspaper-600 mb-4">Articles you archive will appear here.</p>
                </>
              ) : (
                <>
                  <FileText className="w-16 h-16 mx-auto mb-4 text-newspaper-400" />
                  <h3 className="text-xl font-semibold text-newspaper-700 mb-2">
                    {searchTerm ? "No articles found" : "You haven't published yet"}
                  </h3>
                  <p className="text-newspaper-600 mb-4">
                    {searchTerm
                      ? `No articles match "${searchTerm}"`
                      : "Start your literary journey by writing your first article!"}
                  </p>
                  {!searchTerm && (
                    <Link to="/write" className="beat-button inline-flex items-center space-x-2">
                      <PenTool className="w-4 h-4" />
                      <span>Write my first article</span>
                    </Link>
                  )}
                </>
              )}
            </div>
          ) : (
            posts.map((post) => (
              <div
                className={`the-edition-article w-full inline-flex flex-wrap sm:flex-nowrap items-center justify-start gap-[12px] sm:gap-[28px] ${
                  expanded[post.id] ? "mb-[20px]" : "mb-[8px] sm:mb-[20px]"
                }`}
                key={post.id}
                data-property-1="Variant3"
                onClick={() => {
                  const isSmall = typeof window !== "undefined" && window.innerWidth < 640;
                  if (isSmall) {
                    if (!expanded[post.id]) {
                      setExpanded((prev) => ({ ...prev, [post.id]: true }));
                      return;
                    }
                    navigate(`/post/${post.id}`);
                  }
                }}
              >
                <img
                  className={`beat-image-bw ${expanded[post.id] ? "block" : "hidden"} sm:block w-[143px] h-[143px]`}
                  style={{ background: "#D9D9D9", objectFit: "cover" }}
                  src={post.image || "/Beatpost_defaut_image.png"}
                  alt="Beatpost placeholder"
                />
                <div
                  style={{
                    width: "100%",
                    background: "rgba(0, 0, 0, 0)",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: "9px",
                    display: "inline-flex",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    <Link
                      to={`/post/${post.id}`}
                      style={{ textDecoration: "none", flex: 1 }}
                      onClick={(e) => {
                        const isSmall = typeof window !== "undefined" && window.innerWidth < 640;
                        if (isSmall && !expanded[post.id]) {
                          e.preventDefault();
                          setExpanded((prev) => ({ ...prev, [post.id]: true }));
                        }
                      }}
                    >
                      <div
                        style={{
                          alignSelf: "stretch",
                          color: "#262626",
                          fontSize: "20px",
                          fontFamily: "Abhaya Libre",
                          fontWeight: "700",
                          lineHeight: "25px",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.title}
                      </div>
                    </Link>

                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => setEditingPost(post)}
                        className="inline-flex items-center justify-center w-11 h-11 sm:w-auto sm:h-auto p-2 sm:p-1 rounded-md hover:bg-newspaper-50"
                        style={{ background: "transparent", border: "none", cursor: "pointer", color: "#737373" }}
                        title="Edit article"
                        aria-label="Edit article"
                      >
                        <Edit className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleArchivePost(post.id)}
                        className="inline-flex items-center justify-center w-11 h-11 sm:w-auto sm:h-auto p-2 sm:p-1 rounded-md hover:bg-newspaper-50"
                        style={{ background: "transparent", border: "none", cursor: "pointer", color: "#737373" }}
                        title={showArchived ? "Unarchive article" : "Archive article"}
                        aria-label={showArchived ? "Unarchive article" : "Archive article"}
                      >
                        {showArchived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                      </button>

                      <DeletePostButton
                        onConfirm={() => handleDeletePost(post.id)}
                        className="inline-flex items-center justify-center w-11 h-11 sm:w-auto sm:h-auto p-2 sm:p-1 rounded-md hover:bg-newspaper-50 transition-colors"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePost(post.id);
                        }}
                        className="xl:hidden inline-flex items-center justify-center w-11 h-11 sm:w-auto sm:h-auto p-2 sm:p-1 rounded-md hover:bg-newspaper-50"
                        style={{ background: "transparent", border: "none", cursor: "pointer" }}
                        aria-expanded={!!expanded[post.id]}
                        aria-label={expanded[post.id] ? "Collapse" : "Expand"}
                      >
                        {expanded[post.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div
                    className={`${expanded[post.id] ? "block" : "hidden"} sm:block`}
                    style={{
                      alignSelf: "stretch",
                      color: "#525252",
                      fontSize: "14px",
                      fontFamily: "Instrument Sans",
                      fontWeight: "400",
                      lineHeight: "20px",
                      wordWrap: "break-word",
                    }}
                  >
                    {truncateContent(post.content)}
                  </div>

                  <div
                    className={`${expanded[post.id] ? "inline-flex" : "hidden"} sm:inline-flex gap-4`}
                    style={{
                      alignSelf: "stretch",
                      background: "rgba(0, 0, 0, 0)",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        color: "#737373",
                        fontSize: "13px",
                        fontFamily: "Abhaya Libre",
                        fontWeight: "400",
                        lineHeight: "16px",
                        letterSpacing: "0.6px",
                        wordWrap: "break-word",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Clock className="w-3 h-3" />
                      Published{" "}
                      {formatDistanceToNow(new Date(post.created_at), {
                        addSuffix: true,
                        locale: enGB,
                      })}
                    </div>

                    {post.archived && (
                      <div
                        style={{
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre",
                          fontWeight: "400",
                          lineHeight: "16px",
                          letterSpacing: "0.6px",
                          wordWrap: "break-word",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Archive className="w-3 h-3" />
                        Archived
                      </div>
                    )}

                    {post.hashtags.slice(0, 3).map((tag) => (
                      <Link key={tag} to={`/thepulse?hashtag=${tag}`} style={{ textDecoration: "none" }}>
                        <div
                          style={{
                            color: "#A3A3A3",
                            fontSize: "13px",
                            fontFamily: "Abhaya Libre",
                            fontWeight: "400",
                            lineHeight: "16px",
                            wordWrap: "break-word",
                            cursor: "pointer",
                          }}
                        >
                          #{tag}
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div
                    className={`${expanded[post.id] ? "inline-flex" : "hidden"} sm:inline-flex`}
                    style={{
                      alignSelf: "stretch",
                      background: "rgba(0, 0, 0, 0)",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "8px",
                        display: "flex",
                      }}
                    >
                      <Star className="w-4 h-4" style={{ color: "#737373" }} />

                      <div
                        style={{
                          textAlign: "center",
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          lineHeight: "16px",
                          letterSpacing: "0.6px",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.average_rating.toFixed(1)} ({post.ratings_count})
                      </div>
                    </div>
                    <div
                      style={{
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "8px",
                        display: "flex",
                      }}
                    >
                      <Eye className="w-4 h-4" style={{ color: "#737373" }} />

                      <div
                        style={{
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.visits.toLocaleString()} visits
                      </div>
                    </div>
                    <div
                      style={{
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "8px",
                        display: "flex",
                      }}
                    >
                      <MessageCircle className="w-4 h-4" style={{ color: "#737373" }} />

                      <div
                        style={{
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.comments_count} comments
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Mojo Explanation */}
      {/* <div className="p-8 rounded-lg mt-8">
        <h3 className="text-lg font-semibold text-newspaper-900 mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2" />
          Your Beatnik Mojo
        </h3>
        <div className="text-sm text-newspaper-700 space-y-3">
          <p>
            Your current Mojo is <strong>{Math.round(user.mojo)} points</strong>. This index reflects your reputation
            and impact in the Beatpost community.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <h4 className="font-semibold mb-2">How it's calculated:</h4>
              <ul className="space-y-1 text-xs">
                <li>
                  • <strong>5 points</strong> for each published article
                </li>
                <li>
                  • <strong>10 points</strong> for each average rating point
                </li>
                <li>
                  • <strong>3 points</strong> for each follower
                </li>
                <li>
                  • <strong>1 point</strong> for each interaction (comment/rating)
                </li>
                <li>
                  • <strong>0.1 points</strong> for each visit to your articles
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Improve your Mojo:</h4>
              <ul className="space-y-1 text-xs">
                <li>• Publish quality content regularly</li>
                <li>• Interact with other beatniks</li>
                <li>• Use relevant hashtags</li>
                <li>• Respond to comments on your articles</li>
                <li>• Share authentic and original content</li>
              </ul>
            </div>
          </div>
          <div className="beat-quote mt-4">
            "True Mojo isn't measured only in numbers, but in the authenticity of your voice"
          </div>
        </div>
      </div> */}

      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={editingProfile}
        onClose={() => setEditingProfile(false)}
        onSuccess={handleProfileUpdateSuccess}
      />

      {/* Followers Modal */}
      <FollowersModal
        isOpen={showFollowersModal}
        onClose={closeFollowersModal}
        username={user?.username || ""}
        type={followersModalType}
      />
    </div>
  );
};

export default MyProfile;
